import { BookOpen, X } from 'lucide-react';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { HELP_BASE_PATH, articleForPath, findArticleBySlug, helpArticlePath, helpArticles } from './helpCenter';
import HelpArticleBadges from './HelpArticleBadges';
import HelpLoadState from './HelpLoadState';
import MarkdownView, { scrollToAnchor } from './MarkdownView';
import { useHelpContent } from './useHelpContent';
import { useHelpArticleVisibility } from './helpVisibility';

interface DrawerTarget {
    /** null = no article covers the page the drawer was opened from. */
    slug: string | null;
    anchor?: string;
}

interface HelpDrawerContextValue {
    /** Open the article for the current page (the "?" button). */
    openForCurrentPage: () => void;
    isOpen: boolean;
}

const HelpDrawerContext = createContext<HelpDrawerContextValue>({
    openForCurrentPage: () => undefined,
    isOpen: false,
});

export const useHelpDrawer = () => useContext(HelpDrawerContext);

/** Contextual help (#2685): the "?" in the top bar opens the article whose
 *  `routes` match the current page in a side panel, so the reader stays on
 *  the screen they are working in. Links inside the panel stay in the panel. */
export const HelpDrawerProvider = ({ children }: { children: React.ReactNode }) => {
    const location = useLocation();
    const [target, setTarget] = useState<DrawerTarget | null>(null);
    const isVisible = useHelpArticleVisibility();

    const openForCurrentPage = useCallback(() => {
        setTarget({ slug: articleForPath(location.pathname, helpArticles.filter(isVisible))?.slug ?? null });
    }, [location.pathname, isVisible]);

    // Navigating elsewhere closes the panel: its article belonged to the page.
    useEffect(() => {
        setTarget(null);
    }, [location.pathname]);

    const value = useMemo(() => ({ openForCurrentPage, isOpen: target !== null }), [openForCurrentPage, target]);

    return (
        <HelpDrawerContext.Provider value={value}>
            {children}
            {target && <HelpDrawerPanel target={target} onNavigate={setTarget} onClose={() => setTarget(null)} />}
        </HelpDrawerContext.Provider>
    );
};

interface HelpDrawerPanelProps {
    target: DrawerTarget;
    onNavigate: (target: DrawerTarget) => void;
    onClose: () => void;
}

const HelpDrawerPanel = ({ target, onNavigate, onClose }: HelpDrawerPanelProps) => {
    const { t } = useTranslation();
    const { content, loading, failed, retry } = useHelpContent();
    const scrollRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLElement>(null);

    // Keyboard and screen-reader users land in the panel, and return to where
    // they were (the "?" button) when it closes.
    useEffect(() => {
        const previous = document.activeElement as HTMLElement | null;
        panelRef.current?.focus();
        return () => previous?.focus?.();
    }, []);
    const isVisible = useHelpArticleVisibility();
    const found = target.slug ? findArticleBySlug(target.slug) : undefined;
    const meta = found && isVisible(found) ? found : undefined;
    const source = meta ? content?.[meta.slug] : undefined;

    useEffect(() => {
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    useEffect(() => {
        if (!source) return;
        if (target.anchor) scrollToAnchor(target.anchor, scrollRef.current);
        else scrollRef.current?.scrollTo({ top: 0 });
    }, [source, target.anchor, target.slug]);

    return (
        <aside
            ref={panelRef}
            tabIndex={-1}
            role={'dialog'}
            aria-label={t('help.title')}
            className={'fixed top-16 right-0 bottom-0 w-full sm:w-[480px] bg-text-contrast shadow-xl z-[1200] flex flex-col outline-none'}
        >
            <header className={'flex items-center gap-2 px-5 py-3 border-b border-foreground/10'}>
                <span className={'grow text-sm font-semibold text-foreground truncate'}>{meta?.title ?? t('help.title')}</span>
                <Link
                    to={meta ? helpArticlePath(meta.slug, target.anchor) : HELP_BASE_PATH}
                    className={'flex items-center gap-1 text-xs text-foreground/60 hover:text-primary'}
                >
                    <BookOpen className={'w-4 h-4'} />
                    {meta ? t('help.open_in_help_center') : t('help.browse_help')}
                </Link>
                <button onClick={onClose} aria-label={t('help.close')} className={'p-1 text-foreground/60 hover:text-foreground'}>
                    <X className={'w-5 h-5'} />
                </button>
            </header>
            <div ref={scrollRef} className={'grow overflow-y-auto px-5 py-4'}>
                {!meta ? (
                    <div className={'text-sm text-foreground/70'}>
                        <p className={'mb-3'}>{t('help.no_article_for_page')}</p>
                        <Link className={'text-primary underline underline-offset-2'} to={HELP_BASE_PATH}>
                            {t('help.browse_help')}
                        </Link>
                    </div>
                ) : (
                    <>
                        <HelpArticleBadges article={meta} />
                        <HelpLoadState loading={loading} failed={failed} retry={retry} />
                        {source !== undefined && (
                            <MarkdownView
                                source={source}
                                slug={meta.slug}
                                skipTitle
                                scrollRoot={scrollRef}
                                onOpenArticle={(slug, anchor) => onNavigate({ slug, anchor })}
                                isVisible={isVisible}
                            />
                        )}
                    </>
                )}
            </div>
        </aside>
    );
};
